import {colors} from "patient-ping-remedy/packages/theme";
import { RiskFlags } from "@/api/dto/dto";
import Icon from "patient-ping-remedy/packages/icon";
import {RiskFlagsWrapper, RiskFlagsBadge} from "@clients/RiskIndicators.styles";
import React, {Fragment, useCallback, useMemo} from "react";
import propsAreEqual from "react-fast-compare";
import {SmallTypography} from "patient-ping-remedy/packages/typography";
import Spacer from "patient-ping-remedy/packages/spacer";
import ToolTip from "patient-ping-remedy/packages/tool-tip";

type Props = {
  riskFlags?: RiskFlags;
  styleType?: RiskFlagsStyle;
};

export enum RiskFlagsDisplay  {
  READMISSION_RISK = 'Readmission risk',
  RECENT_INPATIENT_STAY = 'Recent inpatient stay',
  HIGH_UTILIZER = 'High utilizer',
}

export enum RiskFlagsStyle {
  PRIMARY = 'PRIMARY',
  PRIMARY_WITH_NAME = 'PRIMARY_WITH_NAME',
  SECONDARY = 'SECONDARY',
}

const RiskIndicators = (props: Props) => {
  const getIconClass = useCallback((key: string) => {
    if (key === RiskFlagsDisplay.READMISSION_RISK) {
      return 'repeat-alt';
    } else if (key === RiskFlagsDisplay.HIGH_UTILIZER) {
      return 'arrow-alt-up';
    } else {
      return 'bed';
    }
  }, []);

  const riskFlagKeys = useMemo(() =>
    Object.entries(props.riskFlags || {}).map(([key, value]) => {
      let newKey;
      const currentDate = new Date();
      const dateValue = new Date(value);
      if (dateValue >= currentDate) {
        if (key === "readmissionRiskEndDate") {
          newKey = RiskFlagsDisplay.READMISSION_RISK;
        } else if (key === "highUtilizerEndDate") {
          newKey = RiskFlagsDisplay.HIGH_UTILIZER;
        } else if (key === "recentInpatientStayEndDate") {
          newKey = RiskFlagsDisplay.RECENT_INPATIENT_STAY;
        }
      }
      return newKey;
    }).reverse(),
  [props.riskFlags]
  );


  const renderBadge = (key: RiskFlagsDisplay) => {
    const isSecondary = props.styleType === RiskFlagsStyle.SECONDARY;
    const isPrimaryWithName = props.styleType === RiskFlagsStyle.PRIMARY_WITH_NAME;

    const badgeColor = isSecondary ? colors.teal4 : colors.teal1;
    const iconColor = isSecondary ? colors.white : colors.teal5;
    const backgroundColor = isPrimaryWithName ? colors.teal1 : badgeColor;
    const textColor = isPrimaryWithName ? colors.teal1 : colors.teal1;

    return (
      <RiskFlagsBadge key={key} backgroundColor={backgroundColor}
        textColor={textColor}
        hasNoName={props.styleType != RiskFlagsStyle.PRIMARY_WITH_NAME}>
        <Icon color={iconColor} iconClass={getIconClass(key)} />
        {isPrimaryWithName && (
          <>
            <Spacer itemWidth="0.5rem" />
            <SmallTypography color={colors.teal5}>{key}</SmallTypography>
          </>
        )}
      </RiskFlagsBadge>
    );
  };

  return (
    <RiskFlagsWrapper className={"risk-indicator"}>
      {riskFlagKeys.map(
        (key) =>
          key && (
            <Fragment key={key}>
              {props.styleType !== RiskFlagsStyle.PRIMARY_WITH_NAME ? (
                <ToolTip position="top" type="dark" text={key}>
                  {renderBadge(key)}
                </ToolTip>
              ) : (
                renderBadge(key)
              )}
            </Fragment>
          )
      )}
    </RiskFlagsWrapper>
  );
};

export default React.memo(RiskIndicators, propsAreEqual);
